// Print Queue Pins (Emin override) — Zustand store. T3.6 Shipping-V1.
//
// Session-local pin state; V1 no backend persistence (refreshing the page
// resets pins). When backend resumes, this store becomes the FE mirror of a
// /api/v1/users/me/print-queue-pins endpoint.
//
// API:
//   pinnedIds       ordered list — first = topmost in Karma sort.
//   pin(id)         appends id (no-op if already pinned).
//   unpin(id)       removes id (no-op if missing).
//   isPinned(id)    boolean lookup.
//   movePinUp(id)   swap with previous neighbour.
//   movePinDown(id) swap with next neighbour.
//   clearPins()     reset to [].

import { create } from 'zustand'

export interface PrintQueuePinsState {
  pinnedIds: string[]
  pin: (id: string) => void
  unpin: (id: string) => void
  isPinned: (id: string) => boolean
  movePinUp: (id: string) => void
  movePinDown: (id: string) => void
  clearPins: () => void
}

export const usePrintQueuePinsStore = create<PrintQueuePinsState>(
  (set, get) => ({
    pinnedIds: [],
    pin: (id) =>
      set((state) => {
        if (state.pinnedIds.includes(id)) return state
        return { pinnedIds: [...state.pinnedIds, id] }
      }),
    unpin: (id) =>
      set((state) => ({
        pinnedIds: state.pinnedIds.filter((p) => p !== id),
      })),
    isPinned: (id) => get().pinnedIds.includes(id),
    movePinUp: (id) =>
      set((state) => {
        const idx = state.pinnedIds.indexOf(id)
        if (idx <= 0) return state
        const next = [...state.pinnedIds]
        ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
        return { pinnedIds: next }
      }),
    movePinDown: (id) =>
      set((state) => {
        const idx = state.pinnedIds.indexOf(id)
        if (idx === -1 || idx === state.pinnedIds.length - 1) return state
        const next = [...state.pinnedIds]
        ;[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]
        return { pinnedIds: next }
      }),
    clearPins: () => set({ pinnedIds: [] }),
  }),
)
